import type { ReactNode } from "react";
import { Lock, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

interface PreferenceSwitchProps {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** When set, the switch cannot be toggled (role-locked preference). */
  disabled?: boolean;
  lockedLabel?: string;
  icon?: LucideIcon;
}

export function PreferenceSwitch({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled = false,
  lockedLabel,
  icon: Icon,
}: PreferenceSwitchProps) {
  return (
    <div className={cn("flex items-start justify-between gap-4", disabled && "opacity-80")}>
      <div className="flex min-w-0 items-start gap-2.5">
        {Icon && (
          <Icon
            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
        )}
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <label htmlFor={id} className="text-sm font-medium leading-tight">
              {label}
            </label>
            {disabled && (
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-1.5 py-0 text-[10px] font-medium text-secondary-foreground">
                <Lock className="size-2.5" />
                {lockedLabel ?? "Locked"}
              </span>
            )}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        disabled={disabled}
        onClick={() => {
          if (!disabled) onCheckedChange(!checked);
        }}
        className={cn(
          "relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          checked ? "bg-primary" : "bg-muted",
          disabled && "cursor-not-allowed opacity-70",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "pointer-events-none block size-5 rounded-full bg-background shadow transition-transform",
            checked ? "translate-x-5" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}

interface PreferenceSectionProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  children: ReactNode;
}

export function PreferenceSection({
  title,
  description,
  icon: Icon,
  children,
}: PreferenceSectionProps) {
  return (
    <section className="rounded-xl border border-border/60 bg-muted/15 p-4">
      <div className="flex items-start gap-3">
        {Icon && (
          <div
            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"
            aria-hidden
          >
            <Icon className="size-4" />
          </div>
        )}
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{title}</h2>
          {description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      <div className="pref-list mt-3">{children}</div>
    </section>
  );
}

interface PreferenceGroupProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  children: ReactNode;
}

/** Labeled subsection inside a preference card. */
export function PreferenceGroup({
  title,
  description,
  icon: Icon,
  children,
}: PreferenceGroupProps) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-start gap-2">
        {Icon && (
          <Icon
            className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
            aria-hidden
          />
        )}
        <div className="min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </h3>
          {description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      <div className="pref-list">{children}</div>
    </div>
  );
}

interface PreferenceRevealProps {
  visible: boolean;
  children: ReactNode;
}

/** Collapses unmatched preference rows without unmounting them. */
export function PreferenceReveal({ visible, children }: PreferenceRevealProps) {
  return (
    <div
      className={cn("pref-reveal", visible && "pref-reveal--open")}
      aria-hidden={!visible}
      inert={!visible || undefined}
    >
      <div className="pref-reveal__inner">{children}</div>
    </div>
  );
}
